import { useString } from "helpers/hooks"
import ModalCustom from "components/ModalCustom"
import FormInputCard from "pages/auth/molecules/FormInputCard"
import Button from "components/Button/Button"
import InputDefault from "components/Input/InputDefault"
import { useEffect } from "react"

import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import MultipleInput from "components/Input/MultipleInput"
import {
  postGroupsOrganizationMiddleware,
  putGroupsOrganizationMiddleware,
} from "../groups.api"
import {
  GroupsOrganizationForm,
  GroupsOrganizationResponse,
} from "../groups.type"

interface Props {
  onCloseModal: () => void
  openModal: boolean
  dataSelected: GroupsOrganizationResponse
  handleWhenSubmit: (response: GroupsOrganizationResponse) => void
}

export const FormGroupsModal = (props: Props) => {
  const { onCloseModal, openModal, dataSelected, handleWhenSubmit } = props
  const name = useString("")
  const description = useString("")

  useEffect(() => {
    if (!openModal) {
      return
    }
    name.setValue(dataSelected.name)
    description.setValue(dataSelected.description)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, dataSelected.id])

  const handleChangeInput = (key: "name" | "description") => (event) => {
    const value = event.target.value
    if (key === "name") {
      name.setValue(value)
      return
    }
    description.setValue(value)
  }

  const onKeyPressInput = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (handleDisableButton()) {
        return
      }
      onSubmit()
    }
  }

  const onSubmit = async () => {
    const dataRequest: GroupsOrganizationForm = {
      name: name.value,
      description: description.value,
    }
    if (dataSelected.id) {
      try {
        const response = await putGroupsOrganizationMiddleware(
          dataSelected.id,
          dataRequest
        )
        toast(
          <LabelNotificationPage
            messenger="Group updated successfully"
            type="success"
          />
        )

        if (response) {
          handleWhenSubmit(response)
        }
      } catch (error: any) {
        toast(
          <LabelNotificationPage
            messenger={
              error.response?.data?.message ?? "Failed to update group"
            }
            type="error"
          />
        )
      }
      return
    }
    try {
      const response = await postGroupsOrganizationMiddleware(dataRequest)
      toast(
        <LabelNotificationPage
          messenger="Group created successfully"
          type="success"
        />
      )

      if (response) {
        handleWhenSubmit(response)
      }
    } catch (error: any) {
      toast(
        <LabelNotificationPage
          messenger={error.response?.data?.message ?? "Failed to create group"}
          type="error"
        />
      )
    }
  }

  const handleDisableButton = (): boolean => {
    if (!name.value.trim()) {
      return true
    }
    return false
  }

  return (
    <ModalCustom
      label={`${dataSelected.id ? "Edit" : "Create new"} group`}
      handleChangeButton={() => onCloseModal}
      bodyChildren={
        <div className="flex flex-col">
          <FormInputCard title="Name" required fontWeightText="font-semibold">
            <InputDefault
              value={name.value}
              onChange={handleChangeInput("name")}
              onKeyPress={onKeyPressInput}
              maxLength={255}
            />
          </FormInputCard>
          <FormInputCard title="Description" fontWeightText="font-semibold">
            <MultipleInput
              value={description.value}
              onChange={handleChangeInput("description")}
              rows={4}
              maxLength={500}
            />
          </FormInputCard>
          <div className="flex items-center justify-between">
            <Button title="Cancel" colorBtn="white" onClick={onCloseModal} />
            <Button
              title={dataSelected.id ? "Save" : "Create"}
              disabled={handleDisableButton()}
              onClick={onSubmit}
            />
          </div>
        </div>
      }
    />
  )
}
